import React, { useContext, useState, useEffect } from "react";
import { Button } from "primereact/button";
import { Accordion, AccordionTab } from "primereact/accordion";
import * as Yup from "yup";
import Input from "../../../../components/form/input";
import InputArea from "../../../../components/form/inputArea";
import DatePicker from "../../../../components/form/DatePicker";
import { formatErrors, handleSubmit } from "../../../../services/utils/processors";
import { ToastContext } from "../../../../services/contexts/toast-context";
import { FormWrapper, FormSection, GridRow, AccordionHeader, AddButtonRow, ActionRow } from "../form.styles";

function formatDateFields(items) {
  return items.map(item => ({
    ...item,
    start_date: item.start_date ? (String(item.start_date).length === 4 ? new Date(`${item.start_date}-01-01`) : new Date(item.start_date)) : null,
    end_date: item.end_date ? (String(item.end_date).length === 4 ? new Date(`${item.end_date}-12-31`) : new Date(item.end_date)) : null,
  }));
}

const persistedFields = ['job_title', 'company', 'start_date', 'end_date', 'city', 'country', 'description'];

const serializeValue = (value, field) => {
  if (value === null || value === undefined) return '';
  if (field === 'start_date' || field === 'end_date') {
    return value instanceof Date
      ? value.toISOString().slice(0, 10)
      : String(value).slice(0, 10);
  }
  return String(value).trim();
};

function getExperiencePayload(current, original) {
  const changed = { id: current.id };

  persistedFields.forEach((field) => {
    if (!original || serializeValue(current[field], field) !== serializeValue(original[field], field)) {
      changed[field] = current[field] ?? null;
    }
  });

  return changed;
}

function checkIfDataChanged(originalData, currentData) {
  const normalize = (items) => items.map(item => persistedFields.reduce((result, field) => ({
    ...result,
    [field]: serializeValue(item[field], field),
  }), {}));
  const serializedOriginal = JSON.stringify(normalize(originalData).sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b))));
  const serializedCurrent = JSON.stringify(normalize(currentData).sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b))));
  return serializedOriginal !== serializedCurrent;
}

const Experience = ({ dbData = null, loading = false, onSave, onBack }) => {
  const toastRef = useContext(ToastContext);
  const baselineData = Array.isArray(dbData) ? dbData : [];
  const baselineDataKey = JSON.stringify(baselineData);
  const [experiences, setExperiences] = useState([]);
  const [initialExperiences, setInitialExperiences] = useState([]);
  const [experienceError, setExperienceError] = useState({});
  const [activeTab, setActiveTab] = useState(0);
  const [isDirty, setIsDirty] = useState(false);

  const schema = Yup.array().of(
    Yup.object({
      job_title: Yup.string().required("Job Title is required"),
      company: Yup.string().required("Company Name is required"),
      start_date: Yup.date().required("Start Date is required"),
      end_date: Yup.date().nullable(),
      city: Yup.string().nullable(),
      country: Yup.string().required("Country is required"),
      description: Yup.string().required("Job Description is required"),
    })
  );

  useEffect(() => {
    const formatted = formatDateFields(baselineData);
    const formattedBaseline = formatDateFields(baselineData);
    setExperiences(formatted);
    setInitialExperiences(JSON.parse(JSON.stringify(formattedBaseline)));
    setExperienceError({});
    setActiveTab(0);
  }, [baselineDataKey]);

  useEffect(() => {
    setIsDirty(checkIfDataChanged(initialExperiences, experiences));
  }, [experiences, initialExperiences]);

  const handleInputChange = (value, index, field) => {
    const updated = [...experiences];
    updated[index] = { ...updated[index], [field]: value };
    setExperiences(updated);
  };

  const addExperience = () => {
    setExperiences([...experiences, { job_title: "", company: "", start_date: null, end_date: null, city: "", country: "", description: "" }]);
    setActiveTab(experiences.length);
  };

  const removeExperience = (index) => {
    setExperiences(experiences.filter((_, i) => i !== index));
  };

  const handleSubmitData = async () => {
    const deletedIds = initialExperiences
      .filter((experience) => experience.id && !experiences.some((current) => current.id === experience.id))
      .map((experience) => experience.id);
    const hasErrors = await handleSubmit(experiences, schema, setExperienceError, () => {
      const initialById = new Map(initialExperiences.filter((experience) => experience.id).map((experience) => [experience.id, experience]));
      const items = experiences
        .map((experience) => getExperiencePayload(experience, initialById.get(experience.id)))
        .filter((experience) => !experience.id || Object.keys(experience).length > 1);
      onSave({ items, deletedIds });
    });
    if (hasErrors && Object.entries(hasErrors).length !== 0) {
      const firstErrorKey = Object.keys(hasErrors)[0];
      const firstErrorIndex = Number(firstErrorKey.match(/\[(\d+)\]/)?.[1] || 0);
      setActiveTab(firstErrorIndex);
      toastRef.current.show({ severity: 'error', summary: "Error", detail: formatErrors(hasErrors), life: 6000 });
    }
  };

  return (
    <FormWrapper>
      <AddButtonRow>
        <Button type="button" label="Add Experience" icon="pi pi-plus" className="p-button-success" onClick={addExperience} />
      </AddButtonRow>

      <Accordion activeIndex={activeTab} onTabChange={(e) => setActiveTab(e.index)} style={{ width: "100%" }}>
        {experiences.map((exp, index) => (
          <AccordionTab key={index}
            header={
              <AccordionHeader>
                <span>{exp.job_title || `Experience ${index + 1}`}</span>
                <Button type="button" icon="pi pi-trash" className="p-button-rounded p-button-danger p-button-text"
                  onClick={(event) => { event.stopPropagation(); removeExperience(index); }} tooltip="Remove" tooltipOptions={{ position: "top" }} />
              </AccordionHeader>
            }
            style={{ minWidth: "50vw", backgroundColor: "transparent", border: "none", marginBottom: 16 }}
          >
            <FormSection>
              <GridRow>
                <Input value={exp.job_title} label="Job Title" width="100%"
                  onChange={(v) => handleInputChange(v, index, "job_title")} error={experienceError?.[index]?.job_title} />
                <Input value={exp.company} label="Company" width="100%"
                  onChange={(v) => handleInputChange(v, index, "company")} error={experienceError?.[index]?.company} />
              </GridRow>
              <GridRow>
                <DatePicker value={exp.start_date} label="Start Date" width="100%"
                  onChange={(v) => handleInputChange(v, index, "start_date")} error={experienceError?.[index]?.start_date} />
                <DatePicker value={exp.end_date} label="End Date" width="100%"
                  onChange={(v) => handleInputChange(v, index, "end_date")} error={experienceError?.[index]?.end_date} />
              </GridRow>
              <GridRow>
                <Input value={exp.city} label="City" width="100%"
                  onChange={(v) => handleInputChange(v, index, "city")} error={experienceError?.[index]?.city} />
                <Input value={exp.country} label="Country" width="100%"
                  onChange={(v) => handleInputChange(v, index, "country")} error={experienceError?.[index]?.country} />
              </GridRow>
              <InputArea value={exp.description} label="Job Description" width="100%" height="120px"
                onChange={(v) => handleInputChange(v, index, "description")} error={experienceError?.[index]?.description} />
            </FormSection>
          </AccordionTab>
        ))}
      </Accordion>

      <ActionRow>
        {onBack && <Button label="Back" icon="pi pi-arrow-left" severity="secondary" onClick={onBack} disabled={loading} />}
        {isDirty && <Button label="Save" icon="pi pi-save" loading={loading} disabled={loading} className="p-button-success" onClick={handleSubmitData} />}
      </ActionRow>
    </FormWrapper>
  );
};

export default Experience;
